import React, { useEffect, useRef, useState } from 'react';
import { useAtom } from 'jotai';
import { ResidentialIncentiveApiData } from '../../types/ResidentialIncentivesDataType';
import { RowValuesType, Link, Checkbox, CheckboxStateValues } from '@cxc/react-fpds-components';
import Table from '../../components/Table/Table';
import ResidentialIncentiveTableKeys from '../../constants/ResidentialIncentiveTableKeys';
import { CarFront, ChargeStatusCharging } from '@cxc/react-fpds-icons-v1';
import { ResidentialIncentiveStatus } from '../../constants/ResidentialIncentiveStatus';
import { formatCapitalizeFirstLetters } from '../../utils/utils';
import { filterResidentialIncentives } from '../../utils/ResidentialIncentivesDataHelpers';
import {
    residentialIncentivesData,
    filteredResidentialIncentivesData,
    residentialIncentiveApplicableVehicleType,
    residentialIncentiveElectricityProvider,
    residentialIncentiveGrantorType,
    residentialIncentivesDataIndex,
    residentialEquipmentType,
    selectedResidentialIncentiveData,
    zipCode,
} from '../../state/GlobalState';
import { useUniversalContext } from '@cxc/react-universal-components';
import { FPG_PAGE_ROUTE, RESIDENTIAL_INCENTIVES_DETAILS_PAGE_ROUTE } from '../../constants/Routes';
import CircleIcon from '../../components/Icons/CircleIcon';
import { EQUIPMENT_TYPE_CHARGER } from '../../constants/EquipmentConstants';
import { difference } from 'lodash';

interface IncentiveSelectionDependenciesForReset {
    selectedResIncentiveElectricityProvider: string | null;
    selectedResIncentiveApplicableVehicleType: string | null;
    selectedResIncentiveGrantorType: string | null;
    selectedResidentialEligibleEquipment: string | null;
    zipcode: string | null;
}

const resetSelectedIncentivesIfNeeded = (
    currentValues: IncentiveSelectionDependenciesForReset,
    prevValuesRef: React.MutableRefObject<IncentiveSelectionDependenciesForReset>,
    setSelectedResidentialIncentives: React.Dispatch<
        React.SetStateAction<ResidentialIncentiveApiData[]>
    >,
) => {
    const hasValueChanged = (
        Object.keys(currentValues) as Array<keyof IncentiveSelectionDependenciesForReset>
    ).some((key) => prevValuesRef.current[key] !== currentValues[key]);

    if (hasValueChanged) {
        setSelectedResidentialIncentives([]);
        prevValuesRef.current = currentValues;
    }
};

const ResidentialIncentivesTableView = () => {
    const [residentialIncentives] =
        useAtom<ResidentialIncentiveApiData[]>(residentialIncentivesData);
    const [filteredResidentialIncentives, setFilteredResidentialIncentivesData] = useAtom<
        ResidentialIncentiveApiData[]
    >(filteredResidentialIncentivesData);
    const [selectedResIncentiveApplicableVehicleType] = useAtom<string>(
        residentialIncentiveApplicableVehicleType,
    );
    const [selectedResIncentiveElectricityProvider] = useAtom<string>(
        residentialIncentiveElectricityProvider,
    );
    const [selectedResidentialEligibleEquipment] = useAtom<string>(residentialEquipmentType);
    const [selectedResIncentiveGrantorType] = useAtom<string>(residentialIncentiveGrantorType);
    const { navigate } = useUniversalContext();
    const [, setResidentialIncentiveDataIndex] = useAtom<number>(residentialIncentivesDataIndex);
    const [selectedResidentialIncentives, setSelectedResidentialIncentives] = useAtom(
        selectedResidentialIncentiveData,
    );
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [zipcode] = useAtom<string>(zipCode);

    const prevDependencyValuesRef = useRef<IncentiveSelectionDependenciesForReset>({
        selectedResIncentiveElectricityProvider,
        selectedResIncentiveApplicableVehicleType,
        selectedResIncentiveGrantorType,
        selectedResidentialEligibleEquipment,
        zipcode,
    });

    // handles filtering the data
    useEffect(() => {
        const filteredData = filterResidentialIncentives(
            residentialIncentives,
            selectedResIncentiveElectricityProvider,
            selectedResIncentiveApplicableVehicleType,
            selectedResIncentiveGrantorType,
            selectedResidentialEligibleEquipment,
        );
        setFilteredResidentialIncentivesData(filteredData);
    }, [
        selectedResIncentiveElectricityProvider,
        selectedResIncentiveApplicableVehicleType,
        selectedResIncentiveGrantorType,
        selectedResidentialEligibleEquipment,
        residentialIncentives,
        setFilteredResidentialIncentivesData,
        zipcode,
    ]);

    useEffect(() => {
        resetSelectedIncentivesIfNeeded(
            {
                selectedResIncentiveElectricityProvider,
                selectedResIncentiveApplicableVehicleType,
                selectedResIncentiveGrantorType,
                selectedResidentialEligibleEquipment,
                zipcode,
            },
            prevDependencyValuesRef,
            setSelectedResidentialIncentives,
        );
    }, [
        selectedResIncentiveElectricityProvider,
        selectedResIncentiveApplicableVehicleType,
        selectedResIncentiveGrantorType,
        selectedResidentialEligibleEquipment,
        zipcode,
        setSelectedResidentialIncentives,
    ]);

    const handleResidentialIncentivesRowClick = (index: number) => {
        setResidentialIncentiveDataIndex(index);
        navigate(`${FPG_PAGE_ROUTE}${RESIDENTIAL_INCENTIVES_DETAILS_PAGE_ROUTE}`);
    };

    const getStatusText = (available: boolean | undefined): string => {
        if (available === true) return ResidentialIncentiveStatus.Current;
        if (available === false) return ResidentialIncentiveStatus.Closed;
        return 'Unknown';
    };

    const getStatusColor = (available: boolean | undefined): string => {
        if (available === true) return 'green';
        if (available === false) return 'red';
        return 'gray';
    };

    const getTypicalAmountDescription = (typicalAmount: string | null): string => {
        return typicalAmount || 'Not Mentioned';
    };

    const renderEligibleEquipment = (equipmentList: string[]) => {
        return equipmentList.map((equipment) => {
            if (equipment === EQUIPMENT_TYPE_CHARGER) {
                return <ChargeStatusCharging key={equipment} size={'s'} />;
            } else {
                return <CarFront key={equipment} size={'s'} />;
            }
        });
    };

    const getCurrentPageItems = () => {
        const startIndex = (currentPage - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        return filteredResidentialIncentives.slice(startIndex, endIndex);
    };

    const handleCheckboxChange =
        (incentive: ResidentialIncentiveApiData) =>
        (event: React.ChangeEvent<HTMLInputElement>) => {
            if (event.target.checked) {
                setSelectedResidentialIncentives([...selectedResidentialIncentives, incentive]);
            } else {
                setSelectedResidentialIncentives(
                    selectedResidentialIncentives.filter((row) => row !== incentive),
                );
            }
        };

    const handleSelectAllChange = (isChecked: boolean) => {
        const currentPageItems = getCurrentPageItems();
        if (!isChecked) {
            setSelectedResidentialIncentives(
                selectedResidentialIncentives.filter(
                    (incentive) => !currentPageItems.includes(incentive),
                ),
            );
        } else {
            setSelectedResidentialIncentives([
                ...new Set([...selectedResidentialIncentives, ...currentPageItems]),
            ]);
        }
    };

    const getCheckboxValue = (incentive: ResidentialIncentiveApiData) => {
        return selectedResidentialIncentives.indexOf(incentive) === -1
            ? CheckboxStateValues.UNCHECKED
            : CheckboxStateValues.CHECKED;
    };

    const handleItemsPerPageChange = (items: number) => {
        setItemsPerPage(items);
        setCurrentPage(1);
    };

    const isAllChecked = () => {
        const currentPageItems = getCurrentPageItems();
        const isChecked = difference(currentPageItems, selectedResidentialIncentives);
        if (selectedResidentialIncentives.length) {
            return isChecked.length === 0
                ? CheckboxStateValues.CHECKED
                : CheckboxStateValues.UNCHECKED;
        } else {
            return CheckboxStateValues.UNCHECKED;
        }
    };

    const mapResidentialIncentiveDataResponseToTableFormat = (
        incentiveData: ResidentialIncentiveApiData[],
    ) => {
        const incentiveTableData: RowValuesType = [];
        if (Array.isArray(incentiveData)) {
            incentiveData.forEach((incentive: ResidentialIncentiveApiData, index: number) => {
                incentiveTableData.push({
                    rowId: incentive.incentiveName,
                    cellData: [
                        {
                            leadingComponents: (
                                <Checkbox
                                    value="default"
                                    checkboxValue={getCheckboxValue(incentive)}
                                    onChange={handleCheckboxChange(incentive)}
                                />
                            ),
                        },
                        {
                            leadingComponents: (
                                <Link
                                    onClick={() => {
                                        handleResidentialIncentivesRowClick(index);
                                    }}
                                    size="medium"
                                >
                                    <div style={{ cursor: 'pointer', color: '#0564D7' }}>
                                        {incentive.incentiveName}
                                    </div>
                                </Link>
                            ),
                        },
                        {
                            textValue: getStatusText(incentive.available),
                            leadingComponents: (
                                <CircleIcon
                                    size="8px"
                                    color={getStatusColor(incentive.available)}
                                />
                            ),
                        },
                        {
                            description: getTypicalAmountDescription(incentive.typicalAmount),
                        },
                        {
                            description: incentive.grantorName,
                        },
                        {
                            leadingComponents: renderEligibleEquipment(incentive.eligibleEquipment),
                        },
                        {
                            description: formatCapitalizeFirstLetters(incentive.type),
                        },
                    ],
                });
            });
        }
        return incentiveTableData;
    };

    const rowValues = mapResidentialIncentiveDataResponseToTableFormat(
        filteredResidentialIncentives,
    );

    return (
        <>
            {residentialIncentives && residentialIncentives.length > 0 ? (
                <Table
                    headerValues={[
                        {
                            id: 'select-checkbox',
                            leadingComponents: (
                                <Checkbox
                                    value="select-all"
                                    checkboxValue={isAllChecked()}
                                    onChange={(e) => handleSelectAllChange(e.target.checked)}
                                />
                            ),
                            contentAlignment: 'left',
                        },
                        ...ResidentialIncentiveTableKeys,
                    ]}
                    rowValues={rowValues}
                    aria-label="Residential Incentives Table"
                    paginationProps={{
                        isPaginationEnabled: true,
                        tablePage: currentPage,
                        itemsPerPage: 10,
                        totalItems: filteredResidentialIncentives.length,
                        setTablePage: (page) => setCurrentPage(page),
                        setItemsPerPage: handleItemsPerPageChange,
                    }}
                />
            ) : null}
        </>
    );
};

export default ResidentialIncentivesTableView;
